/**
 * What a name from somebody else is allowed to be.
 *
 * Every name this build rolls is already safe, so this exists for the names it did not roll. A peer
 * is a browser somebody else controls, and nothing stops a modified build from putting a thousand
 * characters, a run of newlines or a right-to-left override into the roster field. All three land
 * on the screens of everyone in the room.
 *
 * It cuts rather than refuses. A name is cosmetic, and dropping a connection over one would turn a
 * prank into a way of ending somebody else's game. What comes back is either something printable
 * and short, or the empty string, and the caller decides what an empty name falls back to.
 *
 * There is no markup escaping here on purpose: names reach the DOM through `textContent`, never
 * through `innerHTML`, so the angle bracket is not the threat. Length and direction are.
 */

import { MAX_PLAYER_NAME_CHARS } from '@/identity/playerNameContract';

/**
 * Code point ranges a name may not carry, as numbers rather than as a character class.
 *
 * Written out because a regex of invisible characters is a regex nobody can review: every one of
 * these is unreadable in a diff and several of them are unreadable anywhere.
 */
const UNPRINTABLE: readonly (readonly [number, number])[] = [
  [0x0000, 0x001f], // C0 controls, newlines and tabs among them
  [0x007f, 0x009f], // delete, then the C1 controls
  [0x200b, 0x200f], // zero widths, and the first pair of direction marks
  [0x2028, 0x202e], // line and paragraph separators, embeddings and overrides
  [0x2060, 0x206f], // word joiner, invisible operators, deprecated formatting
  [0xfeff, 0xfeff], // byte order mark, which arrives as a zero-width space
];

/**
 * The controls that are whitespace wearing a control's clothes.
 *
 * They become a space rather than nothing, because a newline between two words is a gap: dropping
 * it outright turns `Soggy` and `Walrus` into one word, and the collapse below then has nothing to
 * work with. Every other invisible character is padding and goes.
 */
const SPACING = new Set([0x0009, 0x000a, 0x000b, 0x000c, 0x000d, 0x2028, 0x2029]);

/**
 * Graphemes rather than code points or units, so a cap cuts where a reader would say a character
 * ends: an accent stays on its letter and an emoji built from four code points survives whole.
 */
const GRAPHEMES = new Intl.Segmenter('en', { granularity: 'grapheme' });

export function sanitizePlayerName(raw: string): string {
  const printable = graphemes(raw).map(readable).join('');
  const collapsed = printable.replace(/\s+/gu, ' ').trim();
  return graphemes(collapsed).slice(0, MAX_PLAYER_NAME_CHARS).join('');
}

function graphemes(text: string): readonly string[] {
  return [...GRAPHEMES.segment(text)].map((piece) => piece.segment);
}

/**
 * Judged on what a grapheme starts with, which is what makes it the character it is. A joiner in
 * the middle of an emoji sequence is part of that emoji; a joiner on its own is padding.
 */
function readable(grapheme: string): string {
  const code = grapheme.codePointAt(0) ?? 0;
  if (SPACING.has(code)) return ' ';
  return UNPRINTABLE.some(([low, high]) => code >= low && code <= high) ? '' : grapheme;
}
